import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Badge, Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  FaFileInvoice,
  FaExternalLinkAlt,
  FaUndo,
  FaDollarSign,
  FaCheckCircle,
  FaTimesCircle,
  FaEnvelope,
  FaDownload,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTheme } from '../../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  adminFetchPayments,
  adminRefundPayment,
  adminFetchPaymentStats,
  adminSendInvoiceEmail,
} from '../../../features/subscriptions/subscriptionsSlice';
import type { Payment } from '../../../features/subscriptions/subscriptionsService';

const PAYMENT_STATUS_VARIANTS: Record<string, string> = {
  paid: 'success',
  failed: 'danger',
  refunded: 'warning',
  pending: 'secondary',
};

const PaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useAppDispatch();
  const { adminPayments, adminPaymentsPagination, adminPaymentStats, isLoading, error } = useAppSelector(
    (state) => state.subscriptions,
  );

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState<number | null>(null);

  useEffect(() => {
    dispatch(adminFetchPaymentStats());
  }, [dispatch]);

  useEffect(() => {
    dispatch(adminFetchPayments({ page, page_size: 20, status: statusFilter || undefined }));
  }, [dispatch, page, statusFilter]);

  const statCurrency = adminPaymentStats?.currency || 'usd';

  const formatPrice = (amount: number, currency?: string) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || statCurrency).toUpperCase(),
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await dispatch(
        adminRefundPayment({
          paymentId: refundTarget.id,
          data: { reason: refundReason || undefined },
        }),
      ).unwrap();
      setRefundTarget(null);
      setRefundReason('');
      dispatch(adminFetchPayments({ page, page_size: 20, status: statusFilter || undefined }));
      dispatch(adminFetchPaymentStats());
    } catch {
      // error handled by redux
    } finally {
      setRefunding(false);
    }
  };

  const handleSendInvoice = async (payment: Payment) => {
    const result = await Swal.fire({
      icon: 'question',
      title: t('send_invoice', 'Send Invoice'),
      text: t('send_invoice_confirm', 'The invoice will be sent to the user via email. Continue?'),
      showCancelButton: true,
      confirmButtonText: t('send', 'Send'),
      cancelButtonText: t('cancel', 'Cancel'),
      background: isDark ? '#1e1e2e' : '#fff',
      color: isDark ? '#e2e8f0' : '#1e293b',
    });

    if (!result.isConfirmed) return;

    setSendingInvoice(payment.id);
    try {
      await dispatch(adminSendInvoiceEmail(payment.id)).unwrap();
      Swal.fire({
        icon: 'success',
        title: t('success', 'Success'),
        text: t('invoice_sent_success', 'Invoice email sent successfully!'),
        timer: 2500,
        showConfirmButton: false,
        background: isDark ? '#1e1e2e' : '#fff',
        color: isDark ? '#e2e8f0' : '#1e293b',
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: t('error', 'Error'),
        text: t('invoice_send_failed', 'Failed to send invoice email.'),
        background: isDark ? '#1e1e2e' : '#fff',
        color: isDark ? '#e2e8f0' : '#1e293b',
      });
    } finally {
      setSendingInvoice(null);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  const statCardStyle: React.CSSProperties = {
    background: 'var(--bg-primary)',
    borderRadius: 20,
    padding: '1.25rem 1.5rem',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const statIconStyle = (color: string): React.CSSProperties => ({
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isDark ? `${color}22` : `${color}15`,
    color: color,
    fontSize: '1.2rem',
    transition: 'all 0.3s ease',
    flexShrink: 0,
  });

  const statItems = [
    {
      label: t('total_revenue', 'Total Revenue'),
      value: adminPaymentStats ? formatPrice(adminPaymentStats.total_revenue) : '—',
      icon: FaDollarSign,
      color: '#3b82f6',
    },
    {
      label: t('paid', 'Paid'),
      value: adminPaymentStats
        ? `${adminPaymentStats.paid_count} · ${formatPrice(adminPaymentStats.paid_total)}`
        : '—',
      icon: FaCheckCircle,
      color: '#22c55e',
    },
    {
      label: t('refunded', 'Refunded'),
      value: adminPaymentStats
        ? `${adminPaymentStats.refunded_count} · ${formatPrice(adminPaymentStats.refunded_total)}`
        : '—',
      icon: FaUndo,
      color: '#f59e0b',
    },
    {
      label: t('failed', 'Failed'),
      value: adminPaymentStats
        ? `${adminPaymentStats.failed_count} · ${formatPrice(adminPaymentStats.failed_total)}`
        : '—',
      icon: FaTimesCircle,
      color: '#ef4444',
    },
  ];

  return (
    <div className="animate-fade-in" style={{ padding: '1.5rem' }}>
      <div className="d-flex align-items-center gap-2 mb-4">
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(82,29,179,0.08)',
            color: 'var(--brand-primary)',
            fontSize: '1.1rem',
          }}
        >
          <FaFileInvoice />
        </div>
        <h3 className="fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>
          {t('payments', 'Payments')}
        </h3>
      </div>

      {/* Stat cards */}
      <Row className="g-3 mb-4">
        {statItems.map((item, idx) => (
          <Col sm={6} lg={3} key={idx}>
            <div style={statCardStyle} className="d-flex align-items-center gap-3">
              <div style={statIconStyle(item.color)}>
                <item.icon />
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 2,
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.value}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* Payments table */}
      <div style={cardStyle}>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
          }}
        >
          <h6 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
            {t('all_payments', 'All Payments')}
          </h6>
          <Form.Select
            size="sm"
            style={{
              width: 160,
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              borderColor: 'var(--border-color)',
              borderRadius: 10,
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('all_statuses', 'All Statuses')}</option>
            <option value="paid">{t('paid', 'Paid')}</option>
            <option value="failed">{t('failed', 'Failed')}</option>
            <option value="refunded">{t('refunded', 'Refunded')}</option>
            <option value="pending">{t('pending', 'Pending')}</option>
          </Form.Select>
        </div>

        {isLoading && adminPayments.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                {[
                  t('user', 'User'),
                  t('company', 'Company'),
                  t('amount', 'Amount'),
                  t('status', 'Status'),
                  t('date', 'Date'),
                  t('invoice', 'Invoice'),
                  t('actions', 'Actions'),
                ].map((label) => (
                  <th
                    key={label}
                    style={{
                      color: 'var(--text-secondary)',
                      borderColor: 'var(--border-color)',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adminPayments.map((payment) => (
                <tr key={payment.id}>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    {payment.user_name || '—'}
                  </td>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    {payment.company_name || `Company #${payment.company_id}`}
                  </td>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)', fontWeight: 600 }}>
                    {formatPrice(payment.amount, payment.currency)}
                  </td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <Badge bg={PAYMENT_STATUS_VARIANTS[payment.status] || 'secondary'}>
                      {t(`payment_status_${payment.status}`, payment.status)}
                    </Badge>
                  </td>
                  <td style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)', fontSize: '0.85rem' }}>
                    {formatDate(payment.paid_at || payment.created_at)}
                  </td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <div className="d-flex align-items-center gap-2">
                      {payment.invoice_url && (
                        <a
                          href={payment.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                          style={{ color: 'var(--brand-primary)', fontSize: '0.82rem' }}
                        >
                          <FaExternalLinkAlt className="me-1" />{t('view', 'View')}
                        </a>
                      )}
                      {payment.invoice_pdf && (
                        <a
                          href={payment.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                          style={{ color: 'var(--brand-primary)', fontSize: '0.82rem' }}
                        >
                          <FaDownload className="me-1" />PDF
                        </a>
                      )}
                      {!payment.invoice_url && !payment.invoice_pdf && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>—</span>
                      )}
                    </div>
                  </td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <div className="d-flex gap-1 flex-wrap">
                      {payment.invoice_url && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="btn-modern"
                          disabled={sendingInvoice === payment.id}
                          onClick={() => handleSendInvoice(payment)}
                          title={t('send_invoice', 'Send Invoice')}
                          style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', borderRadius: 8 }}
                        >
                          {sendingInvoice === payment.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <FaEnvelope className="me-1" /> {t('send_invoice', 'Send Invoice')}
                            </>
                          )}
                        </Button>
                      )}
                      {payment.status === 'paid' && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="btn-modern"
                          onClick={() => {
                            setRefundTarget(payment);
                            setRefundReason('');
                          }}
                          style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', borderRadius: 8 }}
                        >
                          <FaUndo className="me-1" /> {t('refund', 'Refund')}
                        </Button>
                      )}
                      {payment.status === 'refunded' && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                          {t('refunded', 'Refunded')}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {adminPayments.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-4"
                    style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}
                  >
                    {t('no_payments', 'No payments found')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        {adminPaymentsPagination && adminPaymentsPagination.total_pages > 1 && (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              background: 'var(--bg-tertiary)',
            }}
          >
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {t('showing_page', 'Page {{page}} of {{total}}', {
                page: adminPaymentsPagination.current_page,
                total: adminPaymentsPagination.total_pages,
              })}
            </span>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-modern btn-secondary"
                disabled={!adminPaymentsPagination.has_previous}
                onClick={() => setPage(page - 1)}
              >
                {t('previous', 'Previous')}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-modern btn-secondary"
                disabled={!adminPaymentsPagination.has_next}
                onClick={() => setPage(page + 1)}
              >
                {t('next', 'Next')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Refund Modal */}
      <Modal show={!!refundTarget} onHide={() => setRefundTarget(null)} centered className="modern-modal">
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title>{t('refund_payment', 'Refund Payment')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          {refundTarget && (
            <>
              <p style={{ color: 'var(--text-primary)' }}>
                {t('refund_confirm_msg', 'Are you sure you want to refund this payment?')}
              </p>
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: 12,
                  padding: '1rem',
                  marginBottom: '1rem',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('company', 'Company')}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                    {refundTarget.company_name || `#${refundTarget.company_id}`}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('amount', 'Amount')}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                    {formatPrice(refundTarget.amount, refundTarget.currency)}
                  </span>
                </div>
              </div>
              <Form.Group>
                <Form.Label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('refund_reason', 'Reason (optional)')}
                </Form.Label>
                <Form.Select
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  style={{
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)',
                    borderRadius: 10,
                  }}
                >
                  <option value="">{t('requested_by_customer', 'requested_by_customer')}</option>
                  <option value="requested_by_customer">{t('requested_by_customer', 'requested_by_customer')}</option>
                  <option value="duplicate">{t('duplicate', 'duplicate')}</option>
                  <option value="fraudulent">{t('fraudulent', 'fraudulent')}</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end px-4">
          <Button
            variant="outline-secondary"
            className="btn-minimal"
            onClick={() => setRefundTarget(null)}
            disabled={refunding}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="warning" className="btn-minimal" onClick={handleRefund} disabled={refunding}>
            {refunding ? <Spinner animation="border" size="sm" className="me-1" /> : <FaUndo className="me-1" />}
            {t('confirm_refund', 'Confirm Refund')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentsPage;
